<template>
  <div class="home-page">
    <main v-if="routingEntry" class="home-content">
      <div class="state-card state-card--error">
        <b>{{ loading ? '正在查询本次检测' : '暂时无法打开本次检测' }}</b>
        <span v-if="errorMessage">{{ errorMessage }}</span>
        <button v-if="!loading" @click="loadHome">重新查询</button>
        <button v-if="!loading" @click="continueHome">返回首页</button>
      </div>
    </main>
    <main v-else class="home-content">
      <div class="home-hero">
        <div class="home-hero__grid"></div>
        <van-swipe class="home-banner" :autoplay="4000" indicator-color="#ffffff">
          <van-swipe-item v-for="(banner, index) in bannerList" :key="index">
            <img :src="banner" alt="上消化道早期筛查" />
          </van-swipe-item>
        </van-swipe>
      </div>

      <button class="new-test-entry" @click="startNewTest">
        <i><Icon type="ios-add" /></i>
        <span><b>开始新的检测</b><small>填写信息并绑定新的检查单</small></span>
        <Icon type="ios-arrow-forward" />
      </button>

      <section class="section-block">
        <div class="section-title">
          <div><b>我的检测</b><span>查看检测进展及报告</span></div>
          <button @click="go('/projects')">查看全部 <Icon type="ios-arrow-forward" /></button>
        </div>

        <div v-if="loading" class="skeleton-list" aria-label="正在加载检测项目">
          <div v-for="index in 2" :key="index" class="project-skeleton">
            <div class="project-skeleton__head"><i></i><i></i></div>
            <div class="project-skeleton__body"><i></i><div><i></i><i></i><i></i></div></div>
            <div class="project-skeleton__foot"></div>
          </div>
        </div>
        <div v-else-if="errorMessage" class="state-card state-card--error">
          <Icon type="ios-alert-outline" />
          <b>检测项目加载失败</b>
          <span>{{ errorMessage }}</span>
          <button @click="loadHome">重新加载</button>
        </div>
        <div v-else-if="products.length" class="product-list">
          <ProjectCard v-for="product in products" :key="product.id" :product="product" @open="openProduct" />
        </div>
        <div v-else class="empty-card">
          <div class="empty-card__icon"><Icon type="ios-paper-outline" /></div>
          <b>暂无绑定产品</b>
          <span v-if="latestPatient">开始新的检测后，可在这里查看检测进展</span>
          <span v-else>完成信息采集后，可在这里查看检测进展</span>
        </div>
      </section>

      <section class="service-tip">
        <Icon type="ios-shield-checkmark" />
        <div><b>检测信息安全保障</b><span>个人信息和报告仅限本人授权后查看</span></div>
        <button v-if="authMode === 'password'" :disabled="loggingOut" @click="switchAccount">
          {{ loggingOut ? '退出中' : '切换账号' }}
        </button>
      </section>
    </main>
  </div>
</template>

<script>
import Qs from 'qs'
import { ensureH5Session, retainedQuery, readEntryQuery } from '../utils/session'
import { clearAuthSession } from '../utils/auth'
import ProjectCard from '../components/ProjectCard'
import { resolvePidEntry, entryKey, wasEntryHandled, markEntryHandled } from '../utils/entryRouting'

export default {
  name: 'Home',
  components: { ProjectCard },
  data () {
    return {
      loading: true,
      loadInProgress: false,
      routingEntry: !!readEntryQuery(this.$route).pid,
      activeEntryKey: '',
      errorMessage: '',
      products: [],
      latestPatient: null,
      authMode: '',
      loggingOut: false,
      bannerList: [
        'https://easyw.oss-cn-hangzhou.aliyuncs.com/banner/banner_1.png',
        'https://easyw.oss-cn-hangzhou.aliyuncs.com/banner/banner_2.png',
        'https://easyw.oss-cn-hangzhou.aliyuncs.com/banner/banner_4.png'
      ]
    }
  },
  beforeDestroy () {
  },
  beforeRouteEnter (to, from, next) {
    next(vm => vm.loadHome())
  },
  watch: {
    '$route.fullPath' () {
      this.loadHome('route-change')
    }
  },
  methods: {
    async loadHome (reason) {
      if (this.loadInProgress) return
      this.loadInProgress = true
      const routePath = this.$route.fullPath
      this.loading = true
      this.errorMessage = ''
      try {
        const query = readEntryQuery(this.$route)
        console.info('[wpc-h5] home load start', {
          reason: reason || 'beforeRouteEnter',
          hasTicket: !!query.ticket,
          envKey: query.envKey || ''
        })
        const session = await ensureH5Session(this)
        this.authMode = session.authMode || ''
        this.activeEntryKey = entryKey(session)
        this.routingEntry = !!String(query.pid || '').trim() && !wasEntryHandled(this.activeEntryKey)
        if (this.routingEntry) {
          const target = await resolvePidEntry(this.$http, query.pid)
          if (this.$route.fullPath !== routePath) return
          await this.$router.replace({ path: target.path, query: retainedQuery(target.query) })
          markEntryHandled(this.activeEntryKey)
          return
        }
        const results = await Promise.all([
          this.$http.post('/eps/getSnList', Qs.stringify({ pageNum: 1, pageSize: 3 })),
          this.$http.get('/h5/patient/getLatestPatient')
        ])
        if (results[0].result !== '1') {
          throw new Error(results[0].desc || '检测项目加载失败')
        }
        const productPage = results[0].data || null
        const list = productPage && productPage.list ? productPage.list : []
        this.products = list
        this.latestPatient = results[1].result === '1' && results[1].data && results[1].data.ID ? results[1].data : null
      } catch (error) {
        console.error('[wpc-h5] home load failed', error)
        this.errorMessage = error.message || '页面加载失败，请稍后重试'
      } finally {
        this.loading = false
        this.loadInProgress = false
        if (this.$route.path === '/home' && this.$route.fullPath !== routePath) this.loadHome('entry-changed')
      }
    },
    continueHome () {
      markEntryHandled(this.activeEntryKey)
      this.routingEntry = false
      this.loadHome('continue-home')
    },
    go (path) {
      this.$router.push({ path, query: retainedQuery() })
    },
    startNewTest () {
      this.$router.push({
        path: '/intake',
        query: retainedQuery({
          mode: 'create',
          returnTo: this.$route.fullPath
        })
      })
    },
    async switchAccount () {
      if (this.loggingOut) return
      this.loggingOut = true
      try {
        await this.$http.post('/h5/auth/logout', '', { skipAuthRecovery: true })
      } catch (e) {
        // 本地登录态仍需清除，避免失效 token 阻塞切换账号。
      } finally {
        clearAuthSession()
        this.loggingOut = false
        this.$router.replace('/login')
      }
    },
    openProduct (product) {
      this.$router.push({
        path: '/projects/' + encodeURIComponent(product.id),
        query: retainedQuery({ patientId: product.patientId || '', serialNum: product.serialNum || '' })
      })
    }
  }
}
</script>

<style scoped>
.home-page { min-height: 100vh; padding-bottom: calc(82px + constant(safe-area-inset-bottom)); padding-bottom: calc(82px + env(safe-area-inset-bottom)); background: linear-gradient(180deg, #eaf5ff 0, #f4f9ff 270px, #f6f8fb 430px); }
.home-content { max-width: 620px; margin: 0 auto; padding: 14px 12px 0; }
.home-hero { position: relative; padding: 8px; border: 1px solid #b9ddfa; border-radius: 20px; background: linear-gradient(135deg, #dff1ff, #f7fbff); box-shadow: 0 10px 26px rgba(37, 111, 181, .15); }
.home-hero__grid { position: absolute; inset: 0; overflow: hidden; border-radius: 20px; opacity: .5; pointer-events: none; background-image: linear-gradient(rgba(42, 139, 218, .08) 1px, transparent 1px), linear-gradient(90deg, rgba(42, 139, 218, .08) 1px, transparent 1px); background-size: 24px 24px; }
.home-hero__grid::after { position: absolute; top: 18%; right: -16%; width: 170px; height: 170px; border: 1px solid rgba(42, 139, 218, .3); border-radius: 50%; box-shadow: 0 0 0 14px rgba(42, 139, 218, .06), 0 0 0 30px rgba(42, 139, 218, .04); content: ''; }
.home-banner { position: relative; z-index: 1; height: 178px; border-radius: 14px; overflow: hidden; background: #b9ddfa; }
.home-banner img { display: block; width: 100%; height: 178px; object-fit: cover; }
.new-test-entry { width: 100%; min-height: 76px; margin-top: 14px; padding: 13px 15px; border: 1px solid #54aff2; border-radius: 15px; display: flex; align-items: center; gap: 12px; color: #fff; background: linear-gradient(110deg, #007de9, #27a0ff); box-shadow: 0 9px 20px rgba(0, 125, 233, .22); text-align: left; }
.new-test-entry > i:first-child { width: 44px; height: 44px; flex: 0 0 44px; border: 1px solid rgba(255, 255, 255, .65); border-radius: 13px; display: grid; place-items: center; color: #fff; background: rgba(255, 255, 255, .16); font-size: 25px; }
.new-test-entry > span { min-width: 0; flex: 1; }
.new-test-entry b, .new-test-entry small { display: block; }
.new-test-entry b { color: #fff; font-size: 16px; font-weight: 600; }
.new-test-entry small { margin-top: 4px; overflow: hidden; color: rgba(255, 255, 255, .82); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.new-test-entry > i:last-child { flex: 0 0 auto; }
.section-block { margin-top: 22px; padding: 16px 12px 14px; border: 1px solid #dbeafa; border-radius: 17px; background: rgba(255, 255, 255, .96); box-shadow: 0 8px 22px rgba(39, 92, 143, .08); }
.section-title { margin: 0 3px 12px; display: flex; justify-content: space-between; align-items: flex-end; }
.section-title b, .section-title span { display: block; }
.section-title b { font-size: 18px; color: var(--wpc-text); }
.section-title span { color: var(--wpc-text-muted); margin-top: 3px; font-size: 12px; }
.section-title button { padding: 5px 0 5px 12px; border: 0; background: none; color: var(--wpc-text-secondary); font-size: 12px; }
.state-card, .empty-card { background: var(--wpc-card); border-radius: var(--wpc-radius); border: 1px solid #eef1f5; }
.product-list { display: grid; gap: 12px; }
.state-card { padding: 32px 20px; text-align: center; color: #8b95a5; }
.state-card--error { display: flex; flex-direction: column; align-items: center; color: #d45d5d; }
.state-card--error > i { font-size: 38px; }
.state-card--error b { margin-top: 6px; color: var(--wpc-text); font-size: 15px; }
.state-card--error span { margin-top: 5px; font-size: 12px; }
.state-card--error button { min-height: 36px; margin-top: 14px; padding: 7px 22px; border: 1px solid #b9d4f1; border-radius: 8px; color: var(--wpc-primary); background: #fff; }
.skeleton-list { display: grid; gap: 12px; }
.project-skeleton { overflow: hidden; border: 1px solid #eef1f5; border-radius: var(--wpc-radius); background: #fff; }
.project-skeleton i, .project-skeleton__foot { display: block; border-radius: 6px; background: linear-gradient(90deg, #edf0f4 25%, #f7f8fa 50%, #edf0f4 75%); background-size: 200% 100%; animation: skeleton-shimmer 1.4s infinite; }
.project-skeleton__head { padding: 15px; display: flex; justify-content: space-between; }
.project-skeleton__head i:first-child { width: 42%; height: 15px; }
.project-skeleton__head i:last-child { width: 18%; height: 20px; }
.project-skeleton__body { padding: 15px; border-top: 1px solid #f1f3f6; display: flex; gap: 13px; }
.project-skeleton__body > i { width: 88px; height: 68px; flex: 0 0 88px; }
.project-skeleton__body div { flex: 1; padding-top: 3px; }
.project-skeleton__body div i { width: 72%; height: 11px; margin-bottom: 11px; }
.project-skeleton__body div i:nth-child(2) { width: 94%; }
.project-skeleton__body div i:nth-child(3) { width: 64%; }
.project-skeleton__foot { height: 38px; border-radius: 0; opacity: .6; }
.empty-card { padding: 28px 20px; text-align: center; display: flex; flex-direction: column; align-items: center; }
.empty-card__icon { color: #7da6dc; font-size: 42px; }
.empty-card b { margin-top: 3px; font-size: 16px; }
.empty-card span { margin-top: 6px; color: #919aaa; font-size: 12px; }
.service-tip { margin: 14px 0 0; padding: 15px; border: 1px solid #c7e4fb; border-radius: 15px; color: #52749b; background: linear-gradient(120deg, #edf7ff, #f7fbff); display: flex; align-items: center; gap: 10px; box-shadow: 0 7px 18px rgba(45, 112, 176, .09); }
.service-tip > i { font-size: 25px; color: #007de9; }
.service-tip b, .service-tip span { display: block; }
.service-tip > div { min-width: 0; flex: 1; }
.service-tip b { color: #24547e; font-size: 13px; }
.service-tip span { margin-top: 3px; color: #6d8eae; font-size: 11px; }
.service-tip button { flex: 0 0 auto; padding: 6px 0 6px 10px; border: 0; color: var(--wpc-primary); background: transparent; font-size: 12px; }
@keyframes skeleton-shimmer { to { background-position: -200% 0; } }

@media (max-width: 340px) {
  .home-content { padding-right: 10px; padding-left: 10px; }
  .home-banner, .home-banner img { height: 158px; }
  .new-test-entry { padding-right: 11px; padding-left: 11px; gap: 9px; }
  .section-title b { font-size: 17px; }
  .service-tip { align-items: flex-start; }
}
</style>



// WEBPACK FOOTER //
// src/views/Home.vue